import { useCallback, useEffect, useState } from "react";
import clsx from "clsx";
import { Link } from "react-router-dom";
import { api } from "@/lib/apiClient";
import { formatArrivalDayLong, todayLocal } from "@/lib/bookingDates";
import { formatCurrency } from "@/lib/money";
import { usePageHeader } from "@/layout/usePageHeader";
import { DailyYearComparisonSection } from "@/pages/reports/DailyYearComparisonSection";
import { GuestFrequencyReportSection } from "@/pages/reports/GuestFrequencyReportSection";
import { MonthlyReportSection } from "@/pages/reports/MonthlyReportSection";
import { RevenueByYearSection } from "@/pages/reports/RevenueByYearSection";
import { SeasonSnapshot } from "@/pages/reports/SeasonSnapshot";
import { YearlyReportSection } from "@/pages/reports/YearlyReportSection";
import { StatCard } from "@/pages/reports/reportShared";
import type { DailyReport } from "@/types/camping";

type ReportView = "overview" | "season" | "month" | "guests";

const VIEWS: { id: ReportView; label: string }[] = [
  { id: "overview", label: "Überblick" },
  { id: "season", label: "Entwicklung" },
  { id: "month", label: "Monat" },
  { id: "guests", label: "Gäste" },
];

export function ReportsPage() {
  const { setPageMeta } = usePageHeader();
  const today = todayLocal();
  const [view, setView] = useState<ReportView>("overview");
  const [selectedDate, setSelectedDate] = useState(today);
  const [todayReport, setTodayReport] = useState<DailyReport | null>(null);
  const [todayLoading, setTodayLoading] = useState(true);
  const [todayError, setTodayError] = useState<string | null>(null);
  const [dayReport, setDayReport] = useState<DailyReport | null>(null);
  const [dayLoading, setDayLoading] = useState(false);
  const [dayError, setDayError] = useState<string | null>(null);

  const loadToday = useCallback(() => {
    setTodayLoading(true);
    setTodayError(null);
    api
      .get<DailyReport>(`/api/v1/reports/daily?date=${encodeURIComponent(today)}`)
      .then(setTodayReport)
      .catch((err: unknown) => {
        setTodayReport(null);
        setTodayError(err instanceof Error ? err.message : "Tagesvergleich konnte nicht geladen werden.");
      })
      .finally(() => setTodayLoading(false));
  }, [today]);

  const loadDay = useCallback(() => {
    if (selectedDate === today) {
      setDayReport(null);
      setDayError(null);
      setDayLoading(false);
      return;
    }
    setDayLoading(true);
    setDayError(null);
    api
      .get<DailyReport>(`/api/v1/reports/daily?date=${encodeURIComponent(selectedDate)}`)
      .then(setDayReport)
      .catch((err: unknown) => {
        setDayReport(null);
        setDayError(err instanceof Error ? err.message : "Stichtag konnte nicht geladen werden.");
      })
      .finally(() => setDayLoading(false));
  }, [selectedDate, today]);

  useEffect(() => {
    loadToday();
  }, [loadToday]);

  useEffect(() => {
    loadDay();
  }, [loadDay]);

  useEffect(() => {
    setPageMeta({
      title: "Auswertungen",
      subtitle: "Heute gegen das Vorjahr, Platz und Saison",
    });
  }, [setPageMeta]);

  const placeReport = selectedDate === today ? todayReport : dayReport;
  const placeLoading = selectedDate === today ? todayLoading : dayLoading;
  const placeError = selectedDate === today ? todayError : dayError;
  const currency = placeReport?.currency ?? todayReport?.currency ?? "EUR";

  return (
    <div className="space-y-8">
      {todayLoading ? (
        <div className="grid gap-3 lg:grid-cols-3" aria-hidden>
          <div className="h-44 animate-pulse rounded-xl bg-fill-tertiary" />
          <div className="h-44 animate-pulse rounded-xl bg-fill-tertiary" />
          <div className="h-44 animate-pulse rounded-xl bg-fill-tertiary" />
        </div>
      ) : todayError ? (
        <p className="rounded-lg border border-status-red/30 bg-status-red/10 px-4 py-3 text-sm text-status-red">
          {todayError}
        </p>
      ) : todayReport ? (
        <DailyYearComparisonSection report={todayReport} currency={todayReport.currency || "EUR"} />
      ) : null}

      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="-mx-1 overflow-x-auto px-1">
          <div className="eh-segmented eh-segmented--md min-w-max" role="tablist" aria-label="Auswertung">
            {VIEWS.map((item) => (
              <button
                key={item.id}
                type="button"
                role="tab"
                aria-selected={view === item.id}
                className={clsx("eh-segmented-item", view === item.id && "is-active")}
                onClick={() => setView(item.id)}
              >
                {item.label}
              </button>
            ))}
          </div>
        </div>
        <Link to="/reports/preisfindung" className="text-sm font-medium text-accent hover:underline">
          Preisfindung
        </Link>
      </div>

      {view === "overview" ? (
        <div className="space-y-8" role="tabpanel">
          <section className="space-y-4">
            <header className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
              <div>
                <h2 className="text-lg font-semibold text-label">Platz</h2>
                <p className="mt-1 text-sm text-label-secondary">
                  {selectedDate === today
                    ? "Wer gerade da ist, welche Plätze belegt sind und was heute kassiert wurde."
                    : `Stand vom ${formatArrivalDayLong(selectedDate)}. Der Vergleich oben bleibt auf dem heutigen Tag.`}
                </p>
              </div>
              <label className="block text-sm">
                <span className="mb-1 block text-label-secondary">Stichtag</span>
                <input
                  type="date"
                  className="eh-input min-h-[44px] w-full sm:w-auto"
                  value={selectedDate}
                  onChange={(event) => setSelectedDate(event.target.value)}
                />
              </label>
            </header>

            {placeError ? (
              <p className="rounded-lg border border-status-red/30 bg-status-red/10 px-4 py-3 text-sm text-status-red">
                {placeError}
              </p>
            ) : null}

            {placeLoading ? (
              <p className="text-sm text-label-secondary">Platzdaten werden geladen…</p>
            ) : placeReport ? (
              <>
                <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                  <StatCard
                    label="Gäste vor Ort"
                    value={placeReport.guests_on_site.toLocaleString("de-DE")}
                    hint={
                      placeReport.dogs_on_site > 0
                        ? `${placeReport.dogs_on_site.toLocaleString("de-DE")} Hunde`
                        : "Eingecheckte Erwachsene und Kinder."
                    }
                  />
                  <StatCard
                    label="Belegte Stellplätze"
                    value={placeReport.occupied_pitches.toLocaleString("de-DE")}
                    hint={`Erwartet ${placeReport.expected_occupied_pitches.toLocaleString("de-DE")}. Abreisetag zählt als frei.`}
                  />
                  <StatCard
                    label="Abreisen"
                    value={placeReport.departures_count.toLocaleString("de-DE")}
                    hint="Buchungen mit Abreise am Stichtag."
                  />
                  <StatCard
                    label="Zahlungseingänge"
                    value={formatCurrency(placeReport.payments_total, currency)}
                    hint="Nach Kalendertag des Zahlungszeitpunkts."
                  />
                </div>

                {selectedDate !== today && dayReport ? (
                  <DailyYearComparisonSection report={dayReport} currency={dayReport.currency || currency} />
                ) : null}

                <nav className="flex flex-wrap gap-x-4 gap-y-2 text-sm">
                  <Link to="/arrivals" className="font-medium text-accent hover:underline">
                    Anreisen
                  </Link>
                  <Link to="/invoices" className="font-medium text-accent hover:underline">
                    Rechnungen
                  </Link>
                  <Link to="/cashbook" className="font-medium text-accent hover:underline">
                    Kassenjournal
                  </Link>
                </nav>
              </>
            ) : null}
          </section>

          <SeasonSnapshot onOpenDevelopment={() => setView("season")} />
        </div>
      ) : null}

      {view === "season" ? (
        <div className="space-y-10" role="tabpanel">
          <YearlyReportSection />
          <RevenueByYearSection />
        </div>
      ) : null}

      {view === "month" ? (
        <div role="tabpanel">
          <MonthlyReportSection />
        </div>
      ) : null}

      {view === "guests" ? (
        <div role="tabpanel">
          <GuestFrequencyReportSection />
        </div>
      ) : null}
    </div>
  );
}
